import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Message, MessageType } from '../components/message/message.component';

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  private messages = new BehaviorSubject<Message[]>([]);
  private messageIdCounter = 0;

  // Observable for components to subscribe to
  public messages$ = this.messages.asObservable();

  /**
   * Add a new message
   */
  addMessage(
    type: MessageType,
    content: string,
    title?: string,
    options?: Partial<Message>
  ): string {
    const id = `msg-${++this.messageIdCounter}`;

    const message: Message = {
      id,
      type,
      content,
      title,
      dismissible: true,
      autoClose: type !== 'error', // Don't auto-close errors by default
      ...options
    };

    const currentMessages = this.messages.value;
    this.messages.next([...currentMessages, message]);

    return id;
  }

  /**
   * Convenience methods for different message types
   */
  success(content: string, title?: string, options?: Partial<Message>): string {
    return this.addMessage('success', content, title, options);
  }

  error(content: string, title?: string, options?: Partial<Message>): string {
    return this.addMessage('error', content, title, { autoClose: false, ...options });
  }

  warning(content: string, title?: string, options?: Partial<Message>): string {
    return this.addMessage('warning', content, title, options);
  }

  info(content: string, title?: string, options?: Partial<Message>): string {
    return this.addMessage('info', content, title, options);
  }

  /**
   * Remove a specific message by ID
   */
  removeMessage(id: string): void {
    const currentMessages = this.messages.value;
    const filteredMessages = currentMessages.filter(msg => msg.id !== id);
    this.messages.next(filteredMessages);
  }

  /**
   * Clear all messages
   */
  clearAll(): void {
    this.messages.next([]);
  }

  /**
   * Clear messages by type
   */
  clearByType(type: MessageType): void {
    const currentMessages = this.messages.value;
    const filteredMessages = currentMessages.filter(msg => msg.type !== type);
    this.messages.next(filteredMessages);
  }

  /**
   * Get current messages count
   */
  getMessageCount(): number {
    return this.messages.value.length;
  }

  /**
   * Check if there are any error messages
   */
  hasErrors(): boolean {
    return this.messages.value.some(msg => msg.type === 'error');
  }

  /**
   * Get all messages of a specific type
   */
  getMessagesByType(type: MessageType): Message[] {
    return this.messages.value.filter(msg => msg.type === type);
  }
}
